<script setup lang="ts">
  import {
    computed,
    nextTick,
    ref,
    useAttrs,
    useId,
    useSlots,
    useTemplateRef,
    watchEffect,
  } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { VueTelInput } from 'vue-tel-input';
  import type { CountryObject } from 'vue-tel-input';
  import 'vue-tel-input/vue-tel-input.css';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import Loader from '../../Loader/Loader.vue';
  import { LoaderSize } from '../../Loader/Loader.types';
  import { FieldSize } from '../FormField';
  import FormField from '../FormField/FormField.vue';
  import {
    countryChevronClass,
    fieldEndSlotClass,
    phoneInputClass,
    phoneInputSmallClass,
    phoneInputWrapperClass,
    phoneInputWrapperSmallClass,
  } from './PhoneInput.css';
  import { PhoneInputMode } from './PhoneInput.types';
  import type {
    FixedVueTelInputResponse,
    PhoneInputEmits,
    PhoneInputProps,
  } from './PhoneInput.types';

  type PhoneInputInstance = {
    focus: () => void;
    blur: () => void;
    $el?: HTMLElement;
  };

  const props = withDefaults(defineProps<PhoneInputProps>(), {
    placeholder: '',
    disableAutoFormat: false,
    allowAnyCharacters: false,
    optionalLabel: false,
    disabled: false,
    meta: undefined,
    inputType: 'tel',
    defaultCountry: 'it',
    mode: PhoneInputMode.NATIONAL,
    size: FieldSize.MD,
  });

  const emit = defineEmits<PhoneInputEmits>();

  defineOptions({
    inheritAttrs: false,
  });
  const attrs = useAttrs();
  const slots = useSlots();
  const { t } = useLocale('ds-components');

  const autoId = useId();
  const inputId = computed(() => props.id ?? autoId);

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const inputOptions = computed(() => ({
    autocomplete: 'on',
    autofocus: props.autofocus,
    id: inputId.value,
    name: props.name,
    showDialCode: false,
    placeholder: props.placeholder,
    required: props.required,
    type: props.inputType,
    styleClasses: isSmall.value
      ? [phoneInputClass, phoneInputSmallClass]
      : [phoneInputClass],
  }));

  const dropdownOptions = computed(() => ({
    ariaLabel: t('phone_input.country_selector_aria_label'),
    disabled: props.disabled || props.loading,
    showDialCodeInList: true,
    showDialCodeInSelection: true,
    showFlags: true,
    showSearchBox: true,
  }));

  const internalModel = ref<string | undefined>(props.modelValue);

  let isPaste = false;

  const onNativeInput = (event: Event) => {
    isPaste = (event as InputEvent).inputType === 'insertFromPaste';
  };

  const onInput = (number: string, phoneObj: FixedVueTelInputResponse) => {
    internalModel.value = number;

    const wasPaste = isPaste;
    isPaste = false;
    const { formatted, valid } = phoneObj;
    if (
      wasPaste &&
      !props.disableAutoFormat &&
      valid &&
      formatted &&
      formatted !== number
    ) {
      nextTick(() => {
        internalModel.value = formatted;
      });
    }

    if (phoneObj.number || phoneObj.formatted) {
      emit('update:modelValue', phoneObj.number || phoneObj.formatted || '');
      emit('phoneDetails', phoneObj);
    }
  };

  const onValidate = (phoneObj: FixedVueTelInputResponse) => {
    emit('validate', phoneObj);
  };
  const onCountryChanged = (countryObject: CountryObject) => {
    emit('countryChanged', countryObject);
  };

  const input = useTemplateRef<PhoneInputInstance>('input');

  watchEffect(() => {
    if (props.autofocus && input.value) {
      input.value.focus();
    }
  });

  // vue-tel-input binds the inner input from a fixed attribute whitelist that
  // excludes aria-invalid and aria-busy, so they can only be applied
  // imperatively
  watchEffect(() => {
    const nativeInput = input.value?.$el?.querySelector('input');
    if (!nativeInput) return;

    if (props.error) {
      nativeInput.setAttribute('aria-invalid', 'true');
    } else {
      nativeInput.removeAttribute('aria-invalid');
    }

    if (props.loading) {
      nativeInput.setAttribute('aria-busy', 'true');
    } else {
      nativeInput.removeAttribute('aria-busy');
    }
  });
</script>

<template>
  <FormField
    :id="inputId"
    :error="error"
    :warning="warning"
    :label="label"
    :helper-text="helperText"
    :meta="meta"
    :optional-label="optionalLabel"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template #default="{ describedBy, hasError }">
      <div
        :class="[phoneInputWrapperClass, isSmall && phoneInputWrapperSmallClass]"
        :data-invalid="hasError ? '' : undefined"
        :data-disabled="disabled || loading ? '' : undefined"
        @input.capture="onNativeInput"
      >
        <VueTelInput
          ref="input"
          :data-testid="testId"
          :style-classes="[attrs.class as string]"
          :auto-format="!disableAutoFormat"
          :valid-characters-only="!allowAnyCharacters"
          :default-country="defaultCountry"
          :disabled="disabled || loading"
          :input-options="{
            ...inputOptions,
            'aria-describedby': describedBy,
          }"
          :dropdown-options="dropdownOptions"
          :autofocus="autofocus"
          :model-value="internalModel"
          :mode="mode"
          @on-input="onInput"
          @validate="onValidate"
          @countryChanged="onCountryChanged"
          @blur="emit('blur')"
          @focus="emit('focus')"
          @space="emit('space')"
          @enter="emit('enter')"
          @open="emit('open')"
          @close="emit('close')"
        >
          <template #arrow-icon="{ open }">
            <span :class="countryChevronClass">
              <FontIcon
                :icon-name="TUNDR_ICONS.CHEVRON_DOWN"
                :rotation="open ? 180 : 0"
              />
            </span>
          </template>
        </VueTelInput>

        <span v-if="loading" :class="fieldEndSlotClass">
          <Loader :size="LoaderSize.SM" :test-id="`${inputId}-loader`" />
        </span>

        <span v-else-if="slots.iconRight" :class="fieldEndSlotClass">
          <slot name="iconRight"></slot>
        </span>
      </div>
    </template>
  </FormField>
</template>
